import type { CaraWorkspaceFieldsFragment } from '~/api/graphql';
import { neutralColor, parseCssColor } from './css-color';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/** The workspace with the copy of a version that a save returned in place of the one it held. */
export const withSavedArtifact = <W extends { artifacts: Artifact[] }>(
  workspace: W,
  saved: Artifact
): W => ({
  ...workspace,
  artifacts: workspace.artifacts.map((each) =>
    each.id === saved.id && each.version === saved.version ? saved : each
  )
});

/**
 * Whether a design switch or the refit after one wrote the version and nothing has changed it
 * since. The server marks such a version by stamping its editedAt with its createdAt
 * (CaraWorkspaceArtifact.WrittenByDesignChange). Its content need not match the version before:
 * the refit moves sections between pages, and a switch back to a document's own look with nothing
 * edited under the design restores the version from before it.
 */
export const writtenByDesignChange = (artifact: Artifact): boolean =>
  artifact.revision === 0 &&
  !!artifact.editedAt &&
  (artifact.editedAt === artifact.createdAt ||
    new Date(artifact.editedAt).getTime() === new Date(artifact.createdAt).getTime());

/**
 * Whether a version differs from the one before it only in its design, as one a design switch
 * made does. The switch stamps editedAt the way an edit does, though no words changed.
 */
export const changedOnlyDesign = (artifact: Artifact, versions: readonly Artifact[]): boolean => {
  const previous = versions.find(
    (each) => each.id === artifact.id && each.version === artifact.version - 1
  );
  return (
    !!previous &&
    (previous.document?.designId ?? null) !== (artifact.document?.designId ?? null) &&
    withoutDesignRewrites(previous.document?.content) ===
      withoutDesignRewrites(artifact.document?.content)
  );
};

/**
 * The switch moves each brand fallback to the new palette (brand.RebrandFallbacks on the
 * server), and one leaving a Legacy look records the old fallbacks beside them as `--cara-legacy-*`
 * properties (brand.MarkLegacyFallbacks), so neither is an edit.
 * pkg/service/api/brand/testdata/rebrand_fallbacks.json holds this and the server's rewrite to
 * the same cases.
 */
const BRAND_FALLBACK = /var\(\s*(--[\w-]+)\s*,\s*#[0-9a-f]{3}(?:[0-9a-f]{3})?\s*\)/gi;
const LEGACY_MARKER =
  /^\s*--cara-legacy-[\w-]+\s*:\s*#[0-9a-f]{3}(?:[0-9a-f]{3})?(?:\s+#[0-9a-f]{3}(?:[0-9a-f]{3})?)*\s*$/i;

/**
 * A switch away from no design also puts the document's own colors and fonts, as a Legacy look or a
 * None template's fill writes them, where the design reaches them, keeping each as an
 * `--cara-own-*` property (brand.AdoptDesignLook), and the switch back gives them back
 * (brand.RestoreOwnLook), so content is compared as that gives it back.
 * pkg/service/api/brand/testdata/own_look_switches.json holds the server's rewrites both ways.
 */
const OWN_LOOK = '--cara-own-';
// The properties the server keeps as the marker alone, in place of their declaration (setAside).
const SET_ASIDE = new Set(['font-family', 'font-size', 'font']);
const FILL_ROLES = ['--primary-color', '--secondary-color', '--accent-color'];
const ROLE_CALL = /var\(\s*(--[\w-]+)\s*,\s*#[0-9a-f]{3}(?:[0-9a-f]{3})?\s*\)/i;
// A color as RestoreOwnLook compares a value's: a literal, or a call a design switch rewrites.
const COLOR_IN_VALUE =
  /var\(\s*--[\w-]+\s*,\s*#[0-9a-f]{3}(?:[0-9a-f]{3})?\s*\)|#[0-9a-f]{3,8}\b|\b(?:rgb|hsl)a?\([^()]*\)/gi;
const CSS_IDENTIFIER = /^(?:--[\w-]+|-?[a-zA-Z_][\w-]*)$/;

const withoutDesignRewrites = (content: string | undefined) => {
  if (content === undefined) return undefined;
  const edits = declarationBlocks(content)
    .flatMap((block) =>
      designRewrites(cssOf(content, block)).flatMap((edit) => editWithin(block, edit))
    )
    .sort((a, b) => a.start - b.start);
  let result = '';
  let written = 0;
  for (const { start, end, text } of edits) {
    result += content.slice(written, start) + text;
    written = end;
  }
  return (result + content.slice(written)).replace(BRAND_FALLBACK, 'var($1)');
};

type Span = [number, number];
type Edit = { start: number; end: number; text: string };
type Declaration = {
  text: string;
  start: number;
  property: string;
  /** Where the property's name starts and its colon is, outside any comment. */
  name: number;
  colon: number;
  legacy: boolean;
};

/**
 * What the server's switch back to the Legacy look does to one block: each marker taken out, and
 * each own color and font given back as brand.RestoreOwnLook gives it back. Each marker gives back
 * at most the nearest declaration of its property before it.
 */
const designRewrites = (css: string): Edit[] => {
  const declarations = readDeclarations(css);
  const removed = new Set<number>();
  const claimed = new Set<number>();
  const edits: Edit[] = [];
  declarations.forEach((marker, i) => {
    if (marker.legacy) {
      removed.add(i);
      return;
    }
    if (!marker.property.startsWith(OWN_LOOK)) return;
    const property = marker.property.slice(OWN_LOOK.length);
    const live = (d: Declaration) => !d.legacy && d.property === property;
    if (SET_ASIDE.has(property)) {
      if (declarations.some(live)) {
        removed.add(i);
      } else {
        const at = marker.start + marker.name;
        edits.push({ start: at, end: at + OWN_LOOK.length, text: '' });
      }
      return;
    }
    removed.add(i);
    let j = i - 1;
    while (j >= 0 && !live(declarations[j])) j--;
    if (j < 0 || claimed.has(j)) return;
    claimed.add(j);
    const [from, to] = valueSpan(declarations[j]);
    const value = ownValue(
      declarations[j].text.slice(from, to),
      marker.text.slice(...valueSpan(marker))
    );
    if (value !== undefined) {
      edits.push({
        start: declarations[j].start + from,
        end: declarations[j].start + to,
        text: value
      });
    }
  });
  return [...edits, ...removals(declarations, removed)];
};

/**
 * recorded with each color live still holds as the switch wrote it given back, and each other
 * color, one picked under the design, as live has it (brand.ownValue). Undefined unless live reads
 * as recorded apart from its colors.
 */
const ownValue = (live: string, recorded: string) => {
  const liveColors = colorsIn(live);
  const recordedColors = colorsIn(recorded);
  if (
    liveColors.length !== recordedColors.length ||
    withoutColors(live, liveColors) !== withoutColors(recorded, recordedColors)
  ) {
    return undefined;
  }
  let value = '';
  let written = 0;
  recordedColors.forEach(([start, end], k) => {
    const own = recorded.slice(start, end);
    const current = live.slice(...liveColors[k]);
    value += recorded.slice(written, start) + (restyled(current, own) ? own : current);
    written = end;
  });
  return value + recorded.slice(written);
};

/**
 * Whether current is own, or a call to the role the switch gives a color like own (brand.restyled):
 * any color can take a fill's on color, a colored one where its role would read too faint on the fill.
 */
const restyled = (current: string, own: string) => {
  if (current === own) return true;
  const call = ROLE_CALL.exec(current);
  const rgb = parseCssColor(own);
  if (!call || !rgb) return false;
  const onFill = call[1].startsWith('--on-');
  const role = onFill ? `--${call[1].slice('--on-'.length)}` : call[1];
  return (onFill || !neutralColor(rgb)) && FILL_ROLES.includes(role);
};

const colorsIn = (value: string): Span[] =>
  [...syntaxOf(value).matchAll(COLOR_IN_VALUE)].map((m) => [m.index, m.index + m[0].length]);

const withoutColors = (value: string, colors: Span[]) => {
  let skeleton = '';
  let written = 0;
  for (const [start, end] of colors) {
    skeleton += `${value.slice(written, start)}\0`;
    written = end;
  }
  return skeleton + value.slice(written);
};

/** css with its comments and strings blanked, as brand.syntaxOf blanks them. */
const syntaxOf = (css: string) => {
  const syntax = Array.from({ length: css.length }, () => ' ');
  scanCSS(css, (i) => {
    syntax[i] = css[i];
  });
  return syntax.join('');
};

// The server takes each marker out with a semicolon beside it (brand.removals), keeping what lies
// between a style element's text, so content read the same way with its markers taken out is the
// content the server wrote either side of the switch.
const removals = (declarations: Declaration[], removed: Set<number>): Edit[] => {
  const edits: Edit[] = [];
  let kept = false;
  declarations.forEach((declaration, i) => {
    const { start } = declaration;
    const end = start + declaration.text.length;
    if (!removed.has(i)) kept = true;
    else if (kept) edits.push({ start: start - 1, end, text: '' });
    else edits.push({ start, end: i < declarations.length - 1 ? end + 1 : end, text: '' });
  });
  return edits;
};

const readDeclarations = (css: string): Declaration[] => {
  let start = 0;
  return splitDeclarations(css).map((text) => {
    const declaration = { text, start, ...readProperty(text), legacy: LEGACY_MARKER.test(text) };
    start += text.length + 1;
    return declaration;
  });
};

/**
 * A declaration's property, read as brand.readDeclaration reads it: outside comments, and none for
 * a declaration that ends inside a comment or a string.
 */
const readProperty = (text: string) => {
  const none = { property: '', name: 0, colon: 0 };
  let colon = -1;
  const syntax = text.split('');
  const unterminated = scanCSS(
    text,
    (i) => {
      if (colon < 0 && text[i] === ':') colon = i;
    },
    (from, to) => syntax.fill(' ', from, to)
  );
  if (colon < 0 || unterminated) return none;
  const head = syntax.slice(0, colon).join('');
  const property = head.trim();
  if (!CSS_IDENTIFIER.test(property)) return none;
  return {
    property: property.startsWith('--') ? property : property.toLowerCase(),
    name: head.length - head.trimStart().length,
    colon
  };
};

/** Where a declaration's value lies, without the space around it, as brand.valueSpan finds it. */
const valueSpan = ({ text, colon }: Declaration): Span => {
  let from = colon + 1;
  while (from < text.length && HTML_SPACE.includes(text[from])) from++;
  let to = text.length;
  while (to > from && HTML_SPACE.includes(text[to - 1])) to--;
  return [from, to];
};

/** An edit of a block's CSS, as edits of the spans that hold it: its text goes in the first. */
const editWithin = (block: Span[], { start, end, text }: Edit): Edit[] =>
  within(block, start, end).map(([from, to], i) => ({ start: from, end: to, text: i ? '' : text }));

const cssOf = (content: string, block: Span[]) =>
  block.map(([start, end]) => content.slice(start, end)).join('');

/** The part of a block's spans that holds characters start to end of its CSS. */
const within = (block: Span[], start: number, end: number) => {
  const part: Span[] = [];
  let at = 0;
  for (const [from, to] of block) {
    const first = Math.max(start - at, 0);
    const last = Math.min(end - at, to - from);
    if (first < last) part.push([from + first, from + last]);
    at += to - from;
  }
  return part;
};

const HTML_SPACE = ' \t\n\r\f';
// Elements whose content the HTML tokenizer reads as text up to their end tag.
const RAW_TEXT_ELEMENTS = new Set([
  'iframe',
  'noembed',
  'noframes',
  'noscript',
  'plaintext',
  'script',
  'style',
  'textarea',
  'title',
  'xmp'
]);

/**
 * Each element's style attribute value and each rule body of a style element, as the spans that
 * hold its CSS, as brand.declarationBlocks finds them: markup is read as a browser tokenizes it,
 * so text that only looks like a tag, such as a code sample or an escaped example, holds none; an
 * SVG style element's rules count; and a style element's text reads as one style sheet, without
 * the comments and elements an SVG one can hold among it.
 */
const declarationBlocks = (content: string): Span[][] => {
  const blocks: Span[][] = [];
  const styles: Span[][] = [];
  const open: OpenElement[] = [];
  const readText = (start: number, end: number) => {
    const top = open.at(-1);
    if (start === end || top?.name !== 'style' || top.namespace === 'math') return;
    if (!top.text) styles.push((top.text = []));
    top.text.push([start, end]);
  };
  let text = 0;
  let i = 0;
  while ((i = content.indexOf('<', i)) >= 0) {
    const next = content[i + 1] ?? '';
    if (!/[a-z/!?]/i.test(next)) {
      i++;
      continue;
    }
    readText(text, i);
    if (/[a-z]/i.test(next)) {
      const tag = readTag(content, i + 1);
      if (tag.style) blocks.push([tag.style]);
      i = tag.end;
      if (!startTag(open, tag) && RAW_TEXT_ELEMENTS.has(tag.name)) {
        const close = new RegExp(`</${tag.name}[${HTML_SPACE}/>]`, 'gi');
        close.lastIndex = i;
        const end =
          (tag.name === 'plaintext' ? null : close.exec(content))?.index ?? content.length;
        readText(i, end);
        i = end;
      }
    } else if (next === '/' && /[a-z]/i.test(content[i + 2] ?? '')) {
      const tag = readTag(content, i + 2);
      endTag(open, tag.name);
      i = tag.end;
    } else if (content.startsWith('<!--', i)) {
      const close = /--!?>/g;
      close.lastIndex = i + 2;
      const end = close.exec(content);
      i = end ? end.index + end[0].length : content.length;
    } else if (content.startsWith('<![CDATA[', i) && open.at(-1)?.namespace) {
      const close = content.indexOf(']]>', i + 9);
      readText(i + 9, close < 0 ? content.length : close);
      i = close < 0 ? content.length : close + 3;
    } else {
      const end = content.indexOf('>', i + 2);
      i = end < 0 ? content.length : end + 1;
    }
    text = i;
  }
  readText(text, content.length);
  for (const sheet of styles) {
    for (const [start, end] of ruleBodies(cssOf(content, sheet))) {
      blocks.push(within(sheet, start, end));
    }
  }
  return blocks;
};

/**
 * The tag whose name starts at i, read as the HTML tokenizer reads it: its lowercased name, where
 * it ends, the first value of each attribute, whether it closes itself, and the span of its style
 * attribute's quoted value. An unquoted style is left alone, as brand.styleValue leaves it.
 */
const readTag = (content: string, i: number) => {
  const skipSpace = (from: number) => {
    while (from < content.length && HTML_SPACE.includes(content[from])) from++;
    return from;
  };
  const until = (from: number, stops: string) => {
    while (from < content.length && !stops.includes(content[from])) from++;
    return from;
  };
  const nameEnd = until(i, `${HTML_SPACE}/>`);
  const name = asciiLower(content.slice(i, nameEnd));
  const attributes = new Map<string, string>();
  let style: [number, number] | undefined;
  let solidus = -1;
  for (i = skipSpace(nameEnd); i < content.length && content[i] !== '>'; i = skipSpace(i)) {
    const keyStart = i;
    if (content[i] === '=') i++;
    i = until(i, `${HTML_SPACE}/>=`);
    const key = asciiLower(content.slice(keyStart, i));
    let value: [number, number] = [i, i];
    let quoted = false;
    const j = skipSpace(i);
    if (content[j] === '/') {
      solidus = j;
      i = j + 1;
    } else if (content[j] === '=') {
      i = skipSpace(j + 1);
      const quote = content[i];
      if (quote === '"' || quote === "'") {
        const close = content.indexOf(quote, i + 1);
        if (close < 0) return { name, end: content.length, attributes, selfClosing: false };
        value = [i + 1, close];
        quoted = true;
        i = close + 1;
      } else {
        const start = i;
        i = until(i, `${HTML_SPACE}>`);
        value = [start, i];
      }
    } else {
      i = j;
    }
    if (key && !attributes.has(key)) {
      attributes.set(key, content.slice(...value));
      if (key === 'style' && quoted) style = value;
    }
  }
  return i < content.length
    ? { name, end: i + 1, style, attributes, selfClosing: solidus === i - 1 }
    : { name, end: content.length, attributes, selfClosing: false };
};

/**
 * An element a browser holds open, as brand.openElements tracks them to tell HTML from foreign
 * (SVG and MathML) content, where a raw text element of HTML's, such as an SVG <title/>, holds
 * markup like any other.
 */
type OpenElement = {
  name: string;
  namespace: '' | 'svg' | 'math';
  htmlIntegration?: boolean;
  // The spans of the text a style element holds.
  text?: Span[];
};

// The start tags that close foreign elements to open as HTML.
const HTML_BREAKOUTS = new Set(
  `b big blockquote body br center code dd div dl dt em embed h1 h2 h3 h4 h5 h6 head hr i img li
   listing menu meta nobr ol p pre ruby s small span strong strike sub sup table tt u ul var`.split(
    /\s+/
  )
);
const VOID_ELEMENTS = new Set(
  `area base basefont bgsound br col embed frame hr img input keygen link meta param source track
   wbr`.split(/\s+/)
);
const MATH_TEXT_INTEGRATIONS = new Set(['mi', 'mn', 'mo', 'ms', 'mtext']);

const isMathText = (element: OpenElement | undefined) =>
  element?.namespace === 'math' && MATH_TEXT_INTEGRATIONS.has(element.name);
const isIntegration = (element: OpenElement | undefined) =>
  !!element?.htmlIntegration || isMathText(element);

/** Opens the element of a start tag, and reports whether it is foreign, which is never raw text. */
const startTag = (open: OpenElement[], tag: ReturnType<typeof readTag>): boolean => {
  const namespace = foreignNamespace(open.at(-1), tag.name);
  if (namespace) {
    const breaksOut =
      HTML_BREAKOUTS.has(tag.name) ||
      (tag.name === 'font' && ['color', 'face', 'size'].some((key) => tag.attributes.has(key)));
    if (!breaksOut) {
      if (!tag.selfClosing) open.push(foreignElement(tag, namespace));
      return true;
    }
    closeForeign(open);
  }
  if (tag.name === 'svg' || tag.name === 'math') {
    if (!tag.selfClosing) open.push(foreignElement(tag, tag.name));
  } else if (!VOID_ELEMENTS.has(tag.name)) {
    open.push({ name: tag.name, namespace: '' });
  }
  return false;
};

const endTag = (open: OpenElement[], name: string) => {
  if ((name === 'p' || name === 'br') && open.at(-1)?.namespace) closeForeign(open);
  for (let at = open.length - 1; at >= 0; at--) {
    if (open[at].name === name) {
      open.length = at;
      return;
    }
    if (isIntegration(open[at])) return;
  }
};

/** The namespace a start tag named name opens its element in, or none for HTML. */
const foreignNamespace = (top: OpenElement | undefined, name: string) => {
  if (!top?.namespace || top.htmlIntegration) return undefined;
  const html = isMathText(top)
    ? name !== 'mglyph' && name !== 'malignmark'
    : top.namespace === 'math' && top.name === 'annotation-xml' && name === 'svg';
  return html ? undefined : top.namespace;
};

const closeForeign = (open: OpenElement[]) => {
  for (let top = open.at(-1); top?.namespace && !isIntegration(top); ) {
    open.pop();
    top = open.at(-1);
  }
};

const foreignElement = (
  { name, attributes }: ReturnType<typeof readTag>,
  namespace: 'svg' | 'math'
): OpenElement => {
  const encoding = asciiLower(decodeReferences(attributes.get('encoding') ?? ''));
  return {
    name,
    namespace,
    htmlIntegration:
      namespace === 'svg'
        ? ['foreignobject', 'desc', 'title'].includes(name)
        : name === 'annotation-xml' &&
          (encoding === 'text/html' || encoding === 'application/xhtml+xml')
  };
};

/** HTML matches names and values case-insensitively in ASCII only, so İ is no I. */
const asciiLower = (text: string) => text.replace(/[A-Z]+/g, (letters) => letters.toLowerCase());

// The only named references that decode to an ASCII letter, / or +, the characters of an
// encoding compared. Any other leaves a character no such encoding holds, decoded or not.
const NAMED_REFERENCES: Record<string, string> = { 'sol;': '/', 'plus;': '+' };

/**
 * An attribute value with its character references decoded as x/net/html decodes them, as far as
 * comparing it to an encoding needs. A C1 control, which it maps to Windows-1252, decodes to
 * U+FFFD here, as an invalid code point does; neither spells an encoding.
 */
const decodeReferences = (value: string) =>
  value.replace(
    /&(?:#(?:[xX]([\da-fA-F]+)|(\d+));?|([a-zA-Z\d]+;?))/g,
    (reference, hex?: string, decimal?: string, name?: string) => {
      if (name !== undefined) return NAMED_REFERENCES[name] ?? reference;
      const code =
        hex === undefined ? Number.parseInt(decimal ?? '', 10) : Number.parseInt(hex, 16);
      const invalid =
        code === 0 || (code >= 0x80 && code <= 0x9f) || (code >= 0xd800 && code <= 0xdfff);
      return invalid || code > 0x10ffff ? '\uFFFD' : String.fromCodePoint(code);
    }
  );

const CHARACTER_REFERENCE = /&#?\w+;/y;
const QUOTE_REFERENCES: Record<string, string> = {
  '&quot;': '"',
  '&#34;': '"',
  '&#x22;': '"',
  '&apos;': "'",
  '&#39;': "'",
  '&#x27;': "'"
};

/**
 * Calls syntax with the index of each character of css outside comments, strings, escapes and
 * character references, and comment with the span of each comment, as brand.scanCSS does. A style
 * attribute writes its quotes as references, so a quote reference opens and closes a string as the
 * quote would. Reports whether css ends inside a comment, a string or an escape.
 */
const scanCSS = (
  css: string,
  syntax: (i: number) => void,
  comment?: (start: number, end: number) => void
): boolean => {
  const unit = (at: number): [string, number] => {
    CHARACTER_REFERENCE.lastIndex = at;
    const reference = css[at] === '&' ? CHARACTER_REFERENCE.exec(css)?.[0] : undefined;
    return reference
      ? [QUOTE_REFERENCES[reference.toLowerCase()] ?? '', reference.length]
      : [css[at], 1];
  };
  let quote = '';
  for (let i = 0; i < css.length; ) {
    const [c, width] = unit(i);
    let step = width;
    if (c === '\\') {
      if (i + width >= css.length) return true;
      step += unit(i + width)[1];
    } else if (quote) {
      if (c === quote) quote = '';
    } else if (c === '"' || c === "'") {
      quote = c;
    } else if (css.startsWith('/*', i)) {
      const end = css.indexOf('*/', i + 2);
      if (end < 0) return true;
      step = end + 2 - i;
      comment?.(i, i + step);
    } else if (width === 1) {
      syntax(i);
    }
    i += step;
  }
  return quote !== '';
};

const ruleBodies = (css: string) => {
  const bodies: [number, number][] = [];
  let open = -1;
  scanCSS(css, (i) => {
    if (css[i] === '{') {
      open = i + 1;
    } else if (css[i] === '}' && open >= 0) {
      bodies.push([open, i]);
      open = -1;
    }
  });
  return bodies;
};

const splitDeclarations = (block: string) => {
  const declarations: string[] = [];
  let depth = 0;
  let start = 0;
  scanCSS(block, (i) => {
    if (block[i] === '(') {
      depth++;
    } else if (block[i] === ')') {
      depth = Math.max(depth - 1, 0);
    } else if (block[i] === ';' && depth === 0) {
      declarations.push(block.slice(start, i));
      start = i + 1;
    }
  });
  return [...declarations, block.slice(start)];
};
